/**
 * Schemas der Wartungsplanung (Spec 2026-08-03) — in FE und BE verwendet.
 */
import { z } from "zod";

import { onlyMineQuerySchema } from "./onlyMine.ts";

export const SYSTEM_STAGES = [
  "entwicklung",
  "test",
  "vorproduktion",
  "produktiv",
  "sonstiges",
] as const;
export const systemStageSchema = z.enum(SYSTEM_STAGES);

/**
 * Review-Befund 6: die Regex prüft nur die FORM ("JJJJ-MM-TT" mit zwei
 * Ziffern), nicht die kalendarische Gültigkeit — "2026-13-45" oder
 * "2026-02-30" kamen durch und scheiterten erst am `date`-Spaltentyp in
 * Postgres (500 statt 400). `Date.UTC` überläuft überzählige Tage/Monate
 * stillschweigend in den Folgemonat statt einen Fehler zu werfen — deshalb
 * hier der Rückvergleich der geparsten Komponenten gegen die Eingabe.
 */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum im Format JJJJ-MM-TT erwartet")
  .refine((value) => {
    const parts = value.split("-").map(Number) as [number, number, number];
    const [year, month, day] = parts;
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    );
  }, "Datum ist kalendarisch ungültig");

const systemBase = z.object({
  name: z.string().min(1).max(120),
  systemKey: z.string().max(40).optional(),
  stage: systemStageSchema,
  engagementId: z.string().uuid().nullable().optional(),
  proactiveMaintenance: z.boolean().default(false),
  note: z.string().max(1000).optional(),
});

/**
 * Spec E5: Proaktive Wartung verlangt eine Zuständigkeit. Als Verfeinerung
 * hier und zusätzlich als CHECK in der Datenbank — der Import geht nicht
 * durch dieses Schema, die Datenbank fängt ihn.
 */
const requiresEngagement = <
  T extends { proactiveMaintenance?: boolean; engagementId?: string | null },
>(
  value: T,
  ctx: z.RefinementCtx,
): void => {
  if (value.proactiveMaintenance && !value.engagementId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["engagementId"],
      message: "Proaktive Wartung setzt eine zuständige Beauftragung voraus.",
    });
  }
};

export const createSystemSchema = systemBase
  .extend({ customerId: z.string().uuid() })
  .superRefine(requiresEngagement);

/**
 * Keine `requiresEngagement`-Verfeinerung: Beim Ändern sind alle Felder
 * optional, die Verfeinerung sähe nur den gesendeten Ausschnitt statt des
 * gespeicherten Zustands (Teil-Update ohne mitgesendete `engagementId` würde
 * fälschlich durchgehen bzw. ein Umschalten auf proaktiv fälschlich blocken).
 * Die Regel gilt für den Zustand *nach* dem Merge und wird deshalb im Dienst
 * gegen den zusammengeführten Datensatz durchgesetzt sowie zusätzlich vom
 * CHECK `cs_proactive_needs_engagement` in der Datenbank abgesichert.
 */
export const updateSystemSchema = systemBase.partial();

export const upsertSoftwareStateSchema = z.object({
  productId: z.string().uuid(),
  versionId: z.string().uuid(),
  asOf: isoDate,
});

export const createProductSchema = z.object({
  name: z.string().min(1).max(120),
  vendor: z.string().max(80).optional(),
  versionScheme: z.enum(["nummer", "version", "release_und_stand"]).default("version"),
  warnOrangeMonths: z.number().int().min(0).max(120).optional(),
  warnRedMonths: z.number().int().min(0).max(120).optional(),
});

export const createVersionSchema = z.object({
  productId: z.string().uuid(),
  label: z.string().min(1).max(60),
  /** Leer lassen, um den Vorschlag des Servers zu übernehmen. */
  parentVersionId: z.string().uuid().nullable().optional(),
});

export const upsertLifecycleSchema = z.object({
  versionId: z.string().uuid(),
  maintenanceEnd: isoDate,
  granularity: z.enum(["tag", "quartal"]).default("tag"),
  source: z.string().max(300).optional(),
});

export const createMaintenanceEventSchema = z.object({
  systemId: z.string().uuid(),
  title: z.string().min(1).max(200),
  plannedFor: isoDate.optional(),
  note: z.string().max(1000).optional(),
  targets: z
    .array(z.object({ productId: z.string().uuid(), versionId: z.string().uuid() }))
    .min(1),
});

export const completeMaintenanceEventSchema = z.object({
  executedOn: isoDate,
});

/**
 * Vorgang verwerfen — der dritte, bis dahin fehlende Ausgang eines
 * Wartungsvorgangs. Ohne ihn blieb für eine Wartung, die nicht stattfindet,
 * nur „als durchgeführt markieren" — und das schreibt den Softwarestand auf
 * eine Zielversion fort, die nie eingespielt wurde.
 *
 * Die Begründung ist Pflicht und kein optionales Feld: Ein Vorgang, der ohne
 * Wort verschwindet, ist von einem vergessenen nicht zu unterscheiden.
 * `trim()` vor `min(1)`, damit ein Feld voller Leerzeichen nicht als
 * Begründung durchgeht.
 */
export const discardMaintenanceEventSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
});

export const maintenanceEventStatusSchema = z.enum([
  "geplant",
  "durchgefuehrt",
  "verworfen",
]);

export const maintenanceEventTargetSchema = z.object({
  productName: z.string(),
  versionLabel: z.string(),
});

/**
 * Ein Vorgang, wie ihn `GET /systems/:id/events` liefert.
 *
 * Wie bei den Übersichtsformen weiter unten (Befund U2) stand diese Form
 * vorher zweimal von Hand als `interface` — einmal in `events.service.ts`,
 * einmal in `apps/web/src/hooks/useMaintenance.ts`. `discardReason` wäre das
 * dritte Feld gewesen, das an zwei Stellen hätte nachgezogen werden müssen.
 */
export const maintenanceEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  plannedFor: z.string().nullable(),
  executedOn: z.string().nullable(),
  status: maintenanceEventStatusSchema,
  /** Nur bei `status = "verworfen"` gesetzt (DB-CHECK `me_discard_reason_matches_status`). */
  discardReason: z.string().nullable(),
  targets: z.array(maintenanceEventTargetSchema),
});

export const maintenanceEventListResponseSchema = z.object({
  items: z.array(maintenanceEventSchema),
});

/**
 * Ein beim Abschließen bewusst ausgelassener Softwarestand: Das
 * Durchführungsdatum liegt VOR dem Erhebungsdatum des gespeicherten Stands.
 *
 * Ein nachgetragener Vorgang darf eine aktuellere Erfassung nicht
 * überschreiben — sonst macht der Nachtrag die neuere Erhebung kaputt und der
 * Bestand fällt lautlos auf einen älteren Stand zurück. Der Vorgang gilt
 * trotzdem als durchgeführt (er wurde ja durchgeführt); der Anwender erfährt
 * aus dieser Liste, welcher Stand deshalb unangetastet blieb.
 */
export const skippedSoftwareStateSchema = z.object({
  productId: z.string(),
  productName: z.string(),
  /** Zielversion des Vorgangs, die NICHT geschrieben wurde. */
  versionLabel: z.string(),
  /** Erhebungsdatum des bestehenden, neueren Stands. */
  existingAsOf: z.string(),
});

export const completeMaintenanceEventResultSchema = z.object({
  updatedStates: z.number(),
  skippedStates: z.number(),
  skipped: z.array(skippedSoftwareStateSchema),
});

export const discardMaintenanceEventResultSchema = z.object({
  id: z.string(),
  status: maintenanceEventStatusSchema,
});

/**
 * Vorlaufwerte eines Produkts nachträglich ändern (Aufgabe 12, Katalog-Reiter).
 * Beide Felder `nullable` UND `optional`: fehlt das Feld, bleibt der
 * gespeicherte Wert unangetastet; ausdrückliches `null` löscht den
 * produktspezifischen Wert wieder — die globale Schwelle greift dann (Spec §5).
 */
export const updateProductThresholdsSchema = z.object({
  warnOrangeMonths: z.number().int().min(0).max(120).nullable().optional(),
  warnRedMonths: z.number().int().min(0).max(120).nullable().optional(),
});

/**
 * Stammdaten eines Katalogprodukts ändern.
 *
 * **Warum das nachgereicht wurde.** `createProductSchema` kennt `vendor` und
 * `versionScheme` seit jeher, das Update konnte aber nur die beiden
 * Vorlauf-Schwellen. Ein Feld, das sich nur bei der Geburt setzen lässt, ist
 * für alles unerreichbar, was der Import angelegt hat — und der setzt den
 * Anbieter nie. Im Katalog stand deshalb überall „–" (aufgefallen am
 * 2026-08-19 am Bestand der Produktivinstanz).
 *
 * Der **Name** ist bewusst dabei: Beim Trennen eines gemischten Produkts will
 * man den Rest umbenennen („SAP ERP/S4HANA" → „SAP ERP"). Der Dienst zieht
 * `name_normalized` mit und lehnt eine Umbenennung auf einen belegten Namen
 * ab — Zusammenführen ist eine andere, folgenreichere Aktion mit eigenem
 * Endpunkt.
 */
export const updateProductSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  vendor: z.string().max(80).nullable().optional(),
  versionScheme: z
    .enum(["nummer", "version", "release_und_stand"])
    .optional(),
  warnOrangeMonths: z.number().int().min(0).max(120).nullable().optional(),
  warnRedMonths: z.number().int().min(0).max(120).nullable().optional(),
});

/**
 * Versionen aus einem Produkt in ein anderes verschieben — das Gegenstück zum
 * Zusammenführen ganzer Produkte.
 *
 * Gebraucht, weil ein Import zwei Lebenszyklen in einem Produkt ablegen kann,
 * wenn die Quelle sie so führt: „SAP ERP/S4HANA" trug nach dem ersten Lauf die
 * EHP-Linien des klassischen ERP **und** die Jahresreleases von S/4HANA. Die
 * Vorlauf-Schwellen hängen am Produkt, eine Schwelle für zwei Lebenszyklen
 * sagt nichts.
 *
 * `versionIds` nennt die gewählten Versionen; ihre **Nachfahren nimmt der
 * Dienst von selbst dazu**. Eine Version, deren Linie zurückbliebe, wird
 * abgelehnt statt die Kante zu kappen.
 */
export const moveVersionsSchema = z.object({
  fromProductId: z.string().uuid(),
  toProductId: z.string().uuid(),
  versionIds: z.array(z.string().uuid()).min(1),
});

/**
 * Zwei Katalogeinträge zusammenführen (Aufgabe 15, Spec §8): `from`
 * verschwindet, seine Versionen und Stände ziehen nach `into`. Löschen ist
 * über die `onDelete: "restrict"`-Fremdschlüssel gar nicht möglich — das ist
 * das einzige Werkzeug für Dubletten, die beim Tippen entstehen (E9).
 */
export const mergeProductsSchema = z
  .object({
    fromId: z.string().uuid(),
    intoId: z.string().uuid(),
  })
  .refine((v) => v.fromId !== v.intoId, {
    message: "Ein Produkt lässt sich nicht mit sich selbst zusammenführen.",
    path: ["intoId"],
  });

/**
 * Merkmalsdefinitionen (Aufgabe 12, Merkmale-Reiter). Anders als der
 * Softwarekatalog entstehen sie NICHT beim Tippen — die Definitionen werden
 * bewusst in den Einstellungen gepflegt; das Setzen eines Werts an einem
 * System bleibt trotzdem für jeden Angemeldeten offen (Spec E6).
 */
const attributeName = z.string().min(1).max(80);
const attributeValue = z.string().min(1).max(200);

export const createAttributeDefSchema = z.object({
  name: attributeName,
  /** Leer = Freitext (siehe `system_attribute_defs.allowed_values`-Kommentar). */
  allowedValues: z.array(attributeValue).max(50).default([]),
});

export const updateAttributeDefSchema = z.object({
  name: attributeName.optional(),
  allowedValues: z.array(attributeValue).max(50).optional(),
});

export const setSystemAttributeSchema = z.object({
  defId: z.string().uuid(),
  value: attributeValue,
});

// ── Antwort-Schemata der Wartungsübersicht (Seite /wartung) ─────────────────
//
// Bis Befund U2 waren diese Formen zweimal von Hand als `interface`
// geschrieben — einmal in `overview.service.ts`, einmal in
// `apps/web/src/hooks/useMaintenance.ts`. Jedes neue Feld musste an beiden
// Stellen nachgezogen werden. Sie liegen jetzt hier und werden von Front- und
// Backend benutzt.

export const lifecycleStatusSchema = z.enum(["green", "orange", "red", "none"]);
export const lifecycleGranularitySchema = z.enum(["tag", "quartal"]).nullable();

export const expiringRowSchema = z.object({
  systemId: z.string(),
  systemName: z.string(),
  customerId: z.string(),
  customerName: z.string(),
  engagementId: z.string().nullable(),
  engagementName: z.string().nullable(),
  /**
   * Service Lead der Beauftragung (Befund U2): Die Beauftragung heißt
   * standardmäßig „Kundenname (Bereich)" und wiederholte damit in der Liste
   * nur die Nachbarspalte. Der Mensch, den man anspricht, hängt an
   * `engagements.lead_employee_id` — ohne ihn beantwortet die Spalte
   * „Zuständigkeit" die Frage nicht, die sie stellt.
   */
  serviceLeadId: z.string().nullable(),
  serviceLeadName: z.string().nullable(),
  productId: z.string(),
  productName: z.string(),
  versionLabel: z.string(),
  maintenanceEnd: z.string().nullable(),
  granularity: lifecycleGranularitySchema,
  /** Ob `maintenanceEnd` von der Versionslinie geerbt ist statt versionseigen. */
  inheritedFromLine: z.boolean(),
  status: lifecycleStatusSchema,
  stale: z.boolean(),
  expired: z.boolean(),
  asOf: z.string(),
  /** Tatsächlich angewandte Vorlaufwerte — Produktwert, sonst die globale Rückfallgröße. */
  warnOrangeMonths: z.number(),
  warnRedMonths: z.number(),
  /** Ob mindestens einer der beiden Werte vom Produkt kommt (sonst rein global). */
  thresholdsFromProduct: z.boolean(),
  /**
   * Befund U10: Ab wann ein Erhebungsdatum als veraltet gilt (Monate).
   * Die Markierung „Stand veraltet" nannte ihre Schwelle nicht — der
   * Anwender sah, DASS etwas veraltet ist, aber nicht ab wann. Der Wert
   * kommt wie die Vorlaufwerte je Zeile vom Server mit, damit die Oberflaeche
   * ihn nicht als Konstante abschreiben muss (dieselbe Begruendung wie bei
   * `defaults` in GET /maintenance/products).
   */
  staleAfterMonths: z.number(),
});

export const productGroupSchema = z.object({
  productId: z.string(),
  productName: z.string(),
  versionLabel: z.string(),
  maintenanceEnd: z.string().nullable(),
  systemCount: z.number(),
  customerCount: z.number(),
  rows: z.array(expiringRowSchema),
});

export const unownedSystemRowSchema = z.object({
  systemId: z.string(),
  systemName: z.string(),
  customerId: z.string(),
  customerName: z.string(),
});

/**
 * Abfrageparameter von `GET /maintenance/expiring`.
 *
 * `status` ist kein zusätzlicher Filter NEBEN `withinMonths`, sondern schaltet
 * ihn ab: Die Ampel begrenzt sich selbst (rot/orange heißt per Definition
 * „innerhalb der Vorlaufwerte des Produkts"). Trägt ein Produkt eigene
 * Vorlaufwerte über dem gewählten Zeitraum, fielen sonst genau die
 * dringlichsten Zeilen aus der Liste — während die Dashboard-Kachel sie zählt.
 */
export const expiringQuerySchema = z.object({
  segmentId: z.string().uuid().optional(),
  onlyMine: onlyMineQuerySchema,
  withinMonths: z.coerce.number().int().min(1).max(60).default(12),
  groupBy: z.enum(["product"]).optional(),
  status: z.enum(["red", "orange"]).optional(),
});

/** GET /maintenance/expiring — `groupBy=product` liefert `groups`, sonst `items`. */
export const expiringResponseSchema = z.object({
  items: z.array(expiringRowSchema).optional(),
  groups: z.array(productGroupSchema).optional(),
});

/** GET /maintenance/attention — nicht segment-gescoped (siehe overview.service.ts). */
export const maintenanceAttentionResponseSchema = z.object({
  withoutOwner: z.array(unownedSystemRowSchema),
  stale: z.array(expiringRowSchema),
  /**
   * Befund U10: Schwelle auch dann, wenn die Liste leer ist — die Ansicht
   * „Stand veraltet" erklaert ihr Kriterium, bevor der erste Treffer da ist.
   */
  staleAfterMonths: z.number(),
});

/** GET /maintenance/summary — Zähler fürs Dashboard-Widget. */
export const maintenanceSummarySchema = z.object({
  red: z.number(),
  orange: z.number(),
});

// ── Reiter „Systeme" der Kundenakte (GET /customers/:id/systems) ────────────
//
// Diese drei Formen standen bis hierher zweimal von Hand: als `interface` in
// `systems.service.ts` und noch einmal in `apps/web/src/hooks/useMaintenance.ts`.
// `proactiveAutoDisabledAt` wäre das nächste Feld gewesen, das an zwei Stellen
// hätte nachgezogen werden müssen — gleiche Begründung wie bei den
// Übersichtsformen weiter oben (Befund U2).

/** Ein Softwarestand am System, samt wirksamem Wartungsende und Ampel. */
export const systemSoftwareEntrySchema = z.object({
  productId: z.string(),
  productName: z.string(),
  versionId: z.string(),
  versionLabel: z.string(),
  asOf: z.string(),
  maintenanceEnd: z.string().nullable(),
  granularity: lifecycleGranularitySchema,
  /** Ob `maintenanceEnd` von der Versionslinie geerbt ist statt versionseigen. */
  inheritedFromLine: z.boolean(),
  status: lifecycleStatusSchema,
  stale: z.boolean(),
  expired: z.boolean(),
  confirmedById: z.string().nullable(),
  confirmedByName: z.string().nullable(),
  /** Tatsächlich angewandte Vorlaufwerte — Produktwert, sonst die globale Rückfallgröße. */
  warnOrangeMonths: z.number(),
  warnRedMonths: z.number(),
  /** Ob mindestens einer der beiden Werte vom Produkt kommt (sonst rein global). */
  thresholdsFromProduct: z.boolean(),
  /** Befund U10: ab wie vielen Monaten ohne Bestätigung der Stand als veraltet gilt. */
  staleAfterMonths: z.number(),
});

/** Gesetzter Merkmalswert an einem System, samt Definition (Aufgabe 16). */
export const systemAttributeEntrySchema = z.object({
  defId: z.string(),
  name: z.string(),
  allowedValues: z.array(z.string()),
  value: z.string(),
});

export const customerSystemSchema = z.object({
  id: z.string(),
  name: z.string(),
  systemKey: z.string().nullable(),
  stage: systemStageSchema,
  engagementId: z.string().nullable(),
  engagementName: z.string().nullable(),
  proactiveMaintenance: z.boolean(),
  /**
   * Gesetzt, wenn der Trigger `cs_disable_proactive_on_engagement_loss`
   * (Migration 0016/0019) „proaktiv warten" selbsttätig abgeschaltet hat, weil
   * die Beauftragung wegfiel. Kehrt die Zuständigkeit zurück, schaltet sich
   * die proaktive Wartung bewusst NICHT von allein wieder ein — der Merker ist
   * das, was den Verlust in der Oberfläche sichtbar macht, bis jemand den
   * Schalter bewusst wieder setzt.
   */
  proactiveAutoDisabledAt: z.string().nullable(),
  software: z.array(systemSoftwareEntrySchema),
  attributes: z.array(systemAttributeEntrySchema),
});

export const customerSystemsResponseSchema = z.object({
  items: z.array(customerSystemSchema),
});

export type SystemSoftwareEntry = z.infer<typeof systemSoftwareEntrySchema>;
export type SystemAttributeEntry = z.infer<typeof systemAttributeEntrySchema>;
export type CustomerSystem = z.infer<typeof customerSystemSchema>;
export type CustomerSystemsResponse = z.infer<typeof customerSystemsResponseSchema>;

// ── Inbetriebnahme importierter Systeme (Sammelbearbeitung) ─────────────────
//
// Nach einem Import haben die Systeme keine Beauftragung; ihre Ampeln bleiben
// grau und „proaktiv warten" ist gesperrt. Einzeln nachzuziehen heißt: je
// System die Kundenakte öffnen. Diese Formen tragen die Sammelansicht.

/** Ein noch nicht in Betrieb genommenes System (keine Beauftragung ODER nicht proaktiv). */
export const pendingSystemRowSchema = z.object({
  systemId: z.string(),
  systemName: z.string(),
  stage: systemStageSchema,
  customerId: z.string(),
  customerName: z.string(),
  engagementId: z.string().nullable(),
  engagementName: z.string().nullable(),
  proactiveMaintenance: z.boolean(),
  /**
   * Wie bei `customerSystemSchema`: gesetzt, wenn der Schalter dem System
   * selbsttätig abhandengekommen ist. Gerade diese Liste ist der Ort, an dem
   * man ihn wieder setzt — ein automatisch abgeschaltetes System sieht hier
   * sonst aus wie ein frisch importiertes, das nie proaktiv war.
   */
  proactiveAutoDisabledAt: z.string().nullable(),
  /** Wie viele Softwarestände am System hängen — ohne sie bringt „proaktiv" keine Ampel. */
  softwareCount: z.number(),
});

/**
 * Auswählbare Beauftragungen. Sie kommen mit der Liste statt über einen
 * zweiten Endpunkt je Kunde: Eine Beauftragung gehört zu GENAU einem Kunden,
 * die Sammelaktion muss also je Kundengruppe eine andere Auswahl anbieten —
 * ein Nachladen pro aufgeklappter Gruppe wären dieselben Daten in vielen
 * kleinen Rundläufen.
 */
export const pendingEngagementSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string(),
  segmentName: z.string(),
  leadName: z.string().nullable(),
});

export const pendingSystemsResponseSchema = z.object({
  items: z.array(pendingSystemRowSchema),
  engagements: z.array(pendingEngagementSchema),
});

/**
 * Mehrere Systeme in einem Zug ändern. Beide Felder optional — aber mindestens
 * eines muss gesetzt sein, sonst wäre der Aufruf ein Schreibvorgang ohne
 * Wirkung (und ein Audit-Eintrag ohne Zustandsänderung, siehe
 * `upsertSoftwareState`).
 *
 * Die Regel „proaktiv nur mit Beauftragung" (Spec E5) steht NICHT hier als
 * `refine`: Sie gilt für den Zustand NACH dem Zusammenführen mit dem
 * gespeicherten Datensatz, den dieses Schema nicht kennt — dieselbe Falle wie
 * bei `updateSystemSchema`. Durchgesetzt wird sie im Dienst und zusätzlich vom
 * CHECK `cs_proactive_needs_engagement`.
 */
export const bulkUpdateSystemsSchema = z
  .object({
    systemIds: z.array(z.string().uuid()).min(1).max(500),
    engagementId: z.string().uuid().nullable().optional(),
    proactiveMaintenance: z.boolean().optional(),
  })
  .refine(
    (value) => value.engagementId !== undefined || value.proactiveMaintenance !== undefined,
    { message: "Es wurde keine Änderung angegeben.", path: ["engagementId"] },
  );

export const bulkUpdateSystemsResultSchema = z.object({
  updated: z.number(),
});

export type PendingSystemRow = z.infer<typeof pendingSystemRowSchema>;
export type PendingEngagement = z.infer<typeof pendingEngagementSchema>;
export type PendingSystemsResponse = z.infer<typeof pendingSystemsResponseSchema>;
export type BulkUpdateSystemsInput = z.infer<typeof bulkUpdateSystemsSchema>;
export type BulkUpdateSystemsResult = z.infer<typeof bulkUpdateSystemsResultSchema>;

// ── Excel-Import „Kundenübersicht" (Spec §7) ────────────────────────────────
//
// Vorschau und Übernahme liefern dieselben Diagnoselisten; sie standen bis zur
// Import-Maske nur als `interface` in `import.service.ts` und waren damit für
// die Oberfläche nicht wiederverwendbar (die rief den Import per `curl` auf).
// Beide Seiten lesen sie jetzt von hier.

/**
 * Dieselbe KONKRETE Version trägt in der Datei mehrere Wartungsenden.
 *
 * Bewusst nicht auf Ebene der Versionslinie: Das Wartungsende hängt am
 * Patch-Stand, nicht an der Minor-Linie — zwei Versionen derselben Linie mit
 * verschiedenen Enden sind der Normalfall, kein Widerspruch.
 */
export const importConflictSchema = z.object({
  product: z.string(),
  versionLabel: z.string(),
  dates: z.array(z.string()),
});

/** Ablaufspalte war befüllt, ließ sich aber keiner bekannten Form zuordnen. */
export const importUnparsedMaintenanceEndSchema = z.object({
  customerName: z.string(),
  raw: z.string(),
});

/**
 * Ein Wartungsende, das die Datei NICHT durchsetzt, weil an DIESER Version im
 * CRM ein von Hand gepflegtes steht (Spec §7: die Handkorrektur gewinnt).
 *
 * Nur die konkrete Version ist im Weg: Der Import schreibt an sie, nicht an
 * ihre Linie. Ein handgepflegtes Ende an der Linie fasst er gar nicht an — es
 * bleibt der Rückfall für alle Versionen ohne eigenes Ende.
 */
export const importProtectedMaintenanceEndSchema = z.object({
  productName: z.string(),
  versionLabel: z.string(),
  /** Was im CRM steht und stehen bleibt. */
  existingEnd: z.string(),
  existingSource: z.string().nullable(),
  /** Was die Datei setzen würde. */
  fileEnd: z.string(),
});

/**
 * Ein erfasster Softwarestand, den die Datei auf eine ANDERE Version setzen
 * würde — die Datei dreht ihn also zurück oder vor.
 *
 * Die Excel trägt kein Erhebungsdatum je Zelle; der Import stempelt seinen
 * Stichtag auf alles. Ein Stand, den jemand nach dem Stichtag der Datei
 * bestätigt hat (etwa beim Abschließen eines Wartungsvorgangs), ist damit
 * aktueller als die Datei — `skipped` sagt, dass der Datumswächter ihn
 * stehen lässt.
 */
export const importOverwrittenStateSchema = z.object({
  customerName: z.string(),
  productName: z.string(),
  /** Was im CRM erfasst ist. */
  existingVersionLabel: z.string(),
  existingAsOf: z.string(),
  existingConfirmedBy: z.string().nullable(),
  /** Was die Datei sagt. */
  fileVersionLabel: z.string(),
  /** true ⇒ der Datumswächter lässt den erfassten Stand stehen. */
  skipped: z.boolean(),
});

/**
 * Stichtag der Datei — nicht der Tag des Imports. Die Excel wird von Hand
 * gepflegt und ist beim Einspielen oft Wochen alt; „heute" auf jeden Stand
 * zu stempeln behauptet eine Aktualität, die sie nicht hat. Fehlt die
 * Angabe, gilt heute (der ehrliche Fall der Erstbefüllung).
 */
export const importOptionsSchema = z.object({
  asOf: isoDate.optional(),
});

/** Zähler, die Vorschau und Übernahme gemeinsam haben. */
const importCountersShape = {
  /** Der tatsächlich verwendete Stichtag (aufgelöst, nie leer). */
  asOf: z.string(),
  systems: z.number(),
  states: z.number(),
  /** Stände, die der Datumswächter stehen lässt, weil sie neuer sind als der Stichtag. */
  skippedStates: z.number(),
  overwrittenStates: z.array(importOverwrittenStateSchema),
  /** Wartungsenden je konkreter Version, die tatsächlich geschrieben werden (ohne die geschützten). */
  lifecycles: z.number(),
  unmatchedCustomers: z.array(z.string()),
  unparsedMaintenanceEnds: z.array(importUnparsedMaintenanceEndSchema),
  protectedMaintenanceEnds: z.array(importProtectedMaintenanceEndSchema),
};

export const importPreviewSchema = z.object({
  ...importCountersShape,
  conflicts: z.array(importConflictSchema),
});

export const importResultSchema = z.object(importCountersShape);

export type ImportConflict = z.infer<typeof importConflictSchema>;
export type ImportUnparsedMaintenanceEnd = z.infer<
  typeof importUnparsedMaintenanceEndSchema
>;
export type ImportProtectedMaintenanceEnd = z.infer<
  typeof importProtectedMaintenanceEndSchema
>;
export type ImportOverwrittenState = z.infer<typeof importOverwrittenStateSchema>;
export type ImportOptions = z.infer<typeof importOptionsSchema>;
export type ImportPreview = z.infer<typeof importPreviewSchema>;
export type ImportResult = z.infer<typeof importResultSchema>;

export type LifecycleStatus = z.infer<typeof lifecycleStatusSchema>;
export type ExpiringRow = z.infer<typeof expiringRowSchema>;
export type ProductGroup = z.infer<typeof productGroupSchema>;
export type UnownedSystemRow = z.infer<typeof unownedSystemRowSchema>;
export type ExpiringQuery = z.input<typeof expiringQuerySchema>;
export type ExpiringResponse = z.infer<typeof expiringResponseSchema>;
export type MaintenanceAttentionResponse = z.infer<
  typeof maintenanceAttentionResponseSchema
>;
export type MaintenanceSummary = z.infer<typeof maintenanceSummarySchema>;

export type SystemStage = z.infer<typeof systemStageSchema>;
export type CreateSystemInput = z.infer<typeof createSystemSchema>;
export type UpdateSystemInput = z.infer<typeof updateSystemSchema>;
export type UpsertSoftwareStateInput = z.infer<typeof upsertSoftwareStateSchema>;
export type CreateProductInput = z.infer<typeof createProductSchema>;
export type CreateVersionInput = z.infer<typeof createVersionSchema>;
export type MergeProductsInput = z.infer<typeof mergeProductsSchema>;
export type UpsertLifecycleInput = z.infer<typeof upsertLifecycleSchema>;
export type CreateMaintenanceEventInput = z.infer<typeof createMaintenanceEventSchema>;
export type DiscardMaintenanceEventInput = z.infer<typeof discardMaintenanceEventSchema>;
export type MaintenanceEventStatus = z.infer<typeof maintenanceEventStatusSchema>;
export type MaintenanceEventTarget = z.infer<typeof maintenanceEventTargetSchema>;
export type MaintenanceEvent = z.infer<typeof maintenanceEventSchema>;
export type MaintenanceEventListResponse = z.infer<typeof maintenanceEventListResponseSchema>;
export type SkippedSoftwareState = z.infer<typeof skippedSoftwareStateSchema>;
export type CompleteMaintenanceEventResult = z.infer<
  typeof completeMaintenanceEventResultSchema
>;
export type DiscardMaintenanceEventResult = z.infer<
  typeof discardMaintenanceEventResultSchema
>;
export type UpdateProductThresholdsInput = z.infer<typeof updateProductThresholdsSchema>;
export type UpdateProductInput = z.infer<typeof updateProductSchema>;
export type MoveVersionsInput = z.infer<typeof moveVersionsSchema>;
export type CreateAttributeDefInput = z.infer<typeof createAttributeDefSchema>;
export type UpdateAttributeDefInput = z.infer<typeof updateAttributeDefSchema>;
export type SetSystemAttributeInput = z.infer<typeof setSystemAttributeSchema>;
